import { Address, AddressTypeMapper } from '../models/address.interface';
import { UtilSelect } from '../models/utils.interface';

export function getSelectOptions<T extends string>(
  mapper: Record<T, string>,
): { value: T; label: string }[] {
  return Object.entries(mapper).map(([key, value]) => ({
    value: key as T,
    label: value as string,
  }));
}

export function adjustForTimezone(date: Date): string {
  if (!date) {
    throw new Error('Invalid date provided');
  }
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0];
}

export function dateFromMysqlString(mysqlDate: string): Date {
  if (!mysqlDate || !mysqlDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
    throw new Error('Invalid date format. Expected YYYY-MM-DD');
  }
  // Achtung: Date-Objekte nehmen Monate von 0-11!
  const [year, month, day] = mysqlDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function adjustAndFormatTime(utcTime: string | Date): string {
  if (!utcTime) {
    throw new Error('Invalid time provided');
  }

  const date = new Date(utcTime);

  // Zeitzonenanpassung
  const adjustedDate = new Date(
    date.getTime() - date.getTimezoneOffset() * 60000,
  );

  // Stunden und Minuten formatieren
  const hours = date.getHours().toString().padStart(2, '0'); // Führende Null
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${hours}:${minutes}`;
}

export function createDateWithTime(time: string): Date {
  if (!time || !time.match(/^\d{2}:\d{2}(:\d{2})?$/)) {
    throw new Error('Invalid time format. Expected HH:mm or HH:mm:ss');
  }

  const [hours, minutes] = time.split(':').map((v) => Number(v) || 0); // Sichere Umwandlung in number
  const now = new Date();

  return new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    hours,
    minutes,
  );
}

export function generateSteps(
  factors: number[],
  count: number,
): { name: string; value: number }[] {
  const steps: number[] = [];

  for (let i = 1; i <= count; i++) {
    factors.forEach((factor) => {
      steps.push(factor * i);
    });
  }

  // Duplikate entfernen und sortieren
  const uniqueSteps = Array.from(new Set(steps)).sort((a, b) => a - b);

  return uniqueSteps.map((value) => ({
    name: `${value} Personen`,
    value,
  }));
}

export function generateWishTimes(start: string, end: string): UtilSelect[] {
  const times: UtilSelect[] = [];

  // Nur Stunden und Minuten extrahieren
  const [startHour, startMinute] = start.split(':').map(Number);
  const [endHour, endMinute] = end.split(':').map(Number);

  let currentHour = startHour;
  let currentMinute = startMinute;

  while (
    currentHour < endHour ||
    (currentHour === endHour && currentMinute <= endMinute)
  ) {
    const time = `${currentHour.toString().padStart(2, '0')}:${currentMinute
      .toString()
      .padStart(2, '0')}`;
    times.push({ name: `${time} Uhr`, value: time });

    currentMinute += 30;
    if (currentMinute >= 60) {
      currentMinute = 0;
      currentHour++;
    }
  }

  return times;
}

export function formatInvoiceNumber(number: number | string): string {
  return number.toString().padStart(4, '0');
}

export function formatAddressLabel(address: Address): string {
  const addressTypeLabel = AddressTypeMapper[address.address_type];
  const typenameLabel = address.typename ? `${address.typename} ` : '';
  const name = ensureValue(address.name);
  const surname = ensureValue(address.surname);
  const street = ensureValue(address.street);
  const streetNr = ensureValue(address.street_nr);
  const zipcode = ensureValue(address.zipcode);
  const city = ensureValue(address.city);

  return `${addressTypeLabel}: ${typenameLabel}${name} ${surname}, ${street} ${streetNr}, ${zipcode} ${city}`;
}

export function ensureValue(value: string | null | undefined): string {
  return value || '-';
}
